// Column edit dispatcher. Routes by `DdlStructure.kind` to the per-source
// path planners. Edits against `create-index` / `drop-index` source
// structures throw — the column-edit applicator does not target index DDL;
// consumers should pick the right edit op for their structure kind.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Column Edit Applicator.

import type { DdlEdit, DdlStructure, PlannedByteEdit } from '../../types';

import type { DdlEditCtx } from './applyDdlEdit';
import {
  planCreateAdd,
  planCreateDrop,
  planCreateRename,
  planCreateFirstColumn,
} from './columnEdit.createTable';
import { planOnAlterTable } from './columnEdit.alterTable';

type ColumnEdit = Extract<DdlEdit, { op: 'add-column' | 'drop-column' | 'rename-column' }>;

export function planColumnEdit(
  ctx: DdlEditCtx,
  edit: ColumnEdit,
): readonly PlannedByteEdit[] {
  const s = ctx.structure;
  if (s.kind === 'create-table') return planOnCreateTable(ctx, s, edit);
  if (s.kind === 'alter-table') return planOnAlterTable(ctx, s, edit);
  throw new Error(
    `applyDdlEdit: cannot apply column edit '${edit.op}' to ${s.kind} statement`,
  );
}

function planOnCreateTable(
  ctx: DdlEditCtx,
  s: Extract<DdlStructure, { kind: 'create-table' }>,
  edit: ColumnEdit,
): readonly PlannedByteEdit[] {
  if (s.columns.length === 0) {
    if (edit.op === 'add-column') return [planCreateFirstColumn(ctx, s, edit.column)];
    throw new Error(
      `applyDdlEdit: cannot apply column edit to CREATE TABLE with no extracted columns `
        + `(e.g. CREATE TABLE … AS SELECT or LIKE form)`,
    );
  }
  if (edit.op === 'add-column') return [planCreateAdd(ctx, s, edit.column, edit.afterColumn)];
  // The origin the payload carries is WHICH occurrence the name picks, not a
  // quoting style — a CREATE TABLE may declare `Id` and `"Id"` side by side.
  if (edit.op === 'drop-column') {
    return [planCreateDrop(ctx, s, edit.columnName, edit.columnNameOrigin)];
  }
  return [planCreateRename(ctx, s, edit.oldName, edit.newName, edit.oldNameOrigin)];
}
